import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiErrorText, apiRequest } from '../../utils';
import '../../pages/event/MemberHome.css';
import '../../pages/event/CompanyEvents.css';
import './GuessWho.css';

/* Is the tribe actually playing the guess-who — the admin's read of it.

   ★ THE NUMBERS ARE HERE AND NOWHERE ELSE. The board itself shows a player their own tally and
   shows the best-known faces with no tally at all, on the rule that a personal number people can
   see is a culture decision rather than a feature toggle. That rule is about what an EMPLOYER
   puts in front of somebody. The admin who bought the programme already reads an engagement score
   per person one tab away, and "is anybody doing this" cannot be answered without counts — so
   this screen spells out what the game deliberately keeps quiet, and it is the only one that does.

   ★ Everybody who has not played is named too, behind one click. A list of only the players
   answers "who is enjoying it" and never "is my tribe doing this", which is the question.

   ★ Nothing here is clickable through to a profile. Somebody on this list may have turned their
   profile off since the round that counted them, and a row that leads to a 404 is worse than a
   row that leads nowhere.

   No mail and no bell — the whole feature sends neither, including to the people who were
   guessed. "Three colleagues guessed you today" is surveillance, not community. */

// The photo, small, or the initial in its place — a roster row with no photo is the normal case
// here rather than a broken one, exactly as on the people tab this screen sits beside.
const Avatar = ({ person }) => (person.photo_url ? (
  <img className="ce-person__avatar" src={person.photo_url} alt="" />
) : (
  <span className="ce-person__avatar ce-person__avatar--empty">
    {(person.name || '?').trim().charAt(0) || '?'}
  </span>
));

const QuizStats = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [who, setWho] = useState(false);   // the names of everybody who has not played

  useEffect(() => {
    apiRequest('GET', '/company/quiz/stats')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  if (data === null) {
    return error ? <p className="bravos-cal__notice">{error}</p> : <span className="auth__spinner" />;
  }

  const players = data.players || [];
  const recognised = data.recognised || [];
  const idle = data.idle || [];
  /* Out of the rounds that were PLAYED, not out of the roster: it says how hard the questions
     are, and a company where two people play perfectly is not "5% right". */
  const accuracy = data.rounds ? Math.round((data.right / data.rounds) * 100) : 0;

  return (
    <div className="qs">
      <p className="bravos-q__hint">{t('quizStatsNote')}</p>

      <div className="ce-row__top">
        <span className="ce-pill ce-pill--going">
          {t('quizStatsPlaying', { n: players.length, total: data.roster })}
        </span>
        {data.rounds > 0 && (
          <>
            <span className="ce-pill">{t('quizStatsRounds', { count: data.rounds })}</span>
            <span className="ce-pill">{t('quizStatsAccuracy', { n: accuracy })}</span>
          </>
        )}
      </div>

      {players.length === 0 && <p className="ce-note">{t('quizStatsNobody')}</p>}

      {/* Who is playing, most right answers first. The tally people never see about themselves. */}
      {players.length > 0 && (
        <>
          <p className="gw-ask">{t('quizStatsPlayers')}</p>
          {players.map((p) => (
            <div key={p.id} className="qs-row">
              <Avatar person={p} />
              <span className="qs-row__body">
                <span className="qs-name">{p.name}</span>
                <span className="qs-sub">
                  {t('quizScore', { right: p.right, played: p.played })}
                  {p.total > 0 && ` · ${t('quizStatsKnows', { n: p.known, total: p.total })}`}
                  {p.best_streak > 1 && ` · ${t('quizStatsBest', { n: p.best_streak })}`}
                </span>
              </span>
              <span className="qs-n">{p.right}</span>
            </div>
          ))}
        </>
      )}

      {/* And who the tribe knows back: the people the rounds were ABOUT, most often found first.
          Same list the board shows its players, with the two numbers behind it added. */}
      {recognised.length > 0 && (
        <>
          <p className="gw-ask">{t('quizRecognised')}</p>
          <p className="ce-note qs-note">{t('quizRecognisedNote')}</p>
          {recognised.map((p) => (
            <div key={p.id} className="qs-row">
              <Avatar person={p} />
              <span className="qs-row__body">
                <span className="qs-name">{p.name}</span>
                <span className="qs-sub">
                  {t('quizStatsFound', { right: p.right, asked: p.asked })}
                </span>
              </span>
              <span className="qs-n">{p.right}</span>
            </div>
          ))}
        </>
      )}

      {/* The other half of the answer. Behind a click because it is the longer list of the two in
          any company that has just started, and it is nobody's fault. */}
      {idle.length > 0 && (
        <>
          <button type="button" className="qs-more" onClick={() => setWho(!who)}
                  aria-expanded={who}>
            <Icon name={who ? 'angle-small-up' : 'angle-small-down'} className="me-1" />
            {t('quizStatsNotPlayed', { count: idle.length })}
          </button>
          {who && (
            <div className="bravos-tags qs-idle">
              {idle.map((p) => <span key={p.id} className="bravos-tag">{p.name}</span>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuizStats;
